import { Fragment, ReactNode } from "react";
import { Box, FormControlLabel, Radio } from "@mui/material";
import DebouncedTextField from "@/components/DebouncedTextField";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import {
  DETAIL_CARD_BACKGROUND,
  FREE_TEXT_PLACEHOLDER,
  FREE_TEXT_MAX_ROWS,
  OPTION_CONTROL_SX,
  OPTION_ROW_SX,
} from "../readmission-f2f-constant";
import { ChooseOneValue } from "../readmission-f2f-types";

export interface ChooseOneGroupProps {
  title: string;
  options: string[];
  value: ChooseOneValue;
  onChange: (value: ChooseOneValue) => void;
  withFreeText?: boolean;
  detailPlaceholders?: Record<string, string>;
  helperTexts?: Record<string, string>;
  bordered?: boolean;
  children?: ReactNode;
  disabled?: boolean;
  freeTextPlaceholder?: string;
  isInline?: boolean;
  /** Inline message under the title, e.g. when a save needs a selection. */
  error?: string;
}

const ChooseOneGroup = ({
  title,
  options,
  value,
  onChange,
  withFreeText = true,
  detailPlaceholders,
  helperTexts,
  bordered = true,
  children,
  disabled,
  freeTextPlaceholder = FREE_TEXT_PLACEHOLDER,
  isInline = false,
  error,
}: ChooseOneGroupProps) => {
  // The detail belongs to the option it was typed under, so switching options drops it.
  const toggle = (option: string) =>
    onChange({
      selected: value.selected === option ? "" : option,
      freeText: withFreeText ? value.freeText : "",
      detail: "",
    });

  const detailPlaceholder = detailPlaceholders?.[value.selected];
  const helperText = helperTexts?.[value.selected];

  const header = (
    <Box>
      <Text fontWeight="bold">{title}</Text>
      {error && (
        <Text size={12} c="error.main">
          {error}
        </Text>
      )}
    </Box>
  );

  const selectedCard = (detailPlaceholder || helperText) && (
    <Box
      sx={{
        bgcolor: DETAIL_CARD_BACKGROUND,
        borderRadius: 1,
        p: 1.25,
        width: "100%",
      }}
      display="flex"
      flexDirection="column"
      gap={0.75}
    >
      {helperText && <Text size={13}>{helperText}</Text>}
      {detailPlaceholder && isInline && (
        <Text size={13} fontWeight="bold">
          {value.selected}
        </Text>
      )}
      {detailPlaceholder && (
        <DebouncedTextField
          size="small"
          fullWidth
          multiline
          maxRows={FREE_TEXT_MAX_ROWS}
          disabled={disabled}
          placeholder={detailPlaceholder}
          value={value.detail ?? ""}
          onChange={(detail) => onChange({ ...value, detail })}
        />
      )}
    </Box>
  );

  const optionsRow = (
    <Flex
      flexWrap="wrap"
      gap={2}
      justifyContent={isInline ? "flex-end" : undefined}
    >
      {options.map((option) => {
        const isSelected = value.selected === option;
        return (
          <Fragment key={option}>
            <FormControlLabel
              sx={OPTION_ROW_SX}
              control={
                <Radio
                  size="small"
                  disabled={disabled}
                  checked={isSelected}
                  onChange={() => undefined}
                  onClick={() => toggle(option)}
                  sx={OPTION_CONTROL_SX}
                />
              }
              label={<Text>{option}</Text>}
            />
            {/* Full-width so the card drops under its option, ahead of the next one. */}
            {isSelected && !isInline && selectedCard}
          </Fragment>
        );
      })}
    </Flex>
  );

  return (
    <Box
      sx={
        bordered ? { border: "1px solid #E0E0E0", borderRadius: 1, p: 1.5 } : {}
      }
      display="flex"
      flexDirection="column"
      gap={1}
    >
      {isInline ? (
        <>
          <Flex alignItems="center" justifyContent="space-between" gap={2}>
            {header}
            {optionsRow}
          </Flex>
          {value.selected && selectedCard}
        </>
      ) : (
        <>
          {header}
          {optionsRow}
        </>
      )}
      {children}
      {withFreeText && (
        <DebouncedTextField
          size="small"
          fullWidth
          multiline
          maxRows={FREE_TEXT_MAX_ROWS}
          disabled={disabled}
          placeholder={freeTextPlaceholder}
          value={value.freeText}
          onChange={(freeText) => onChange({ ...value, freeText })}
        />
      )}
    </Box>
  );
};

export default ChooseOneGroup;
